import {Table} from "antd";
import className from "classnames/bind";
import {FontAwesomeIcon, Loading} from "~/components";
import {useGetSitemapReportQuery} from "~/reduxs/api/technicalApiSlice";
import {UrlLink, StatusTag, fmt} from "./TechShared";
import style from "../style/Technical.module.scss";

const cn = className.bind(style);

/**
 * Sitemap Manager (#11): đối chiếu URL trong sitemap THẬT (tải live) với tập URL đã crawl
 * của lần crawl mới nhất. Tự fetch (endpoint riêng) khi tab được mở — vì tải sitemap là
 * HTTP live nên không gộp vào báo cáo kỹ thuật chung. Trình bày: thẻ tóm tắt + các bảng
 * theo nhóm vấn đề (thiếu trong sitemap / không craw tới / lỗi / redirect / noindex).
 */
function TechSitemap({projectId}) {

	const {data, isLoading, isError, isFetching} = useGetSitemapReportQuery(projectId);

	if (isLoading || isFetching) return <Loading />;

	if (isError || !data) {
		return <div className={cn('tab')}><p className={cn('muted')}>Không tải được dữ liệu sitemap.</p></div>;
	}

	const sm = data.sitemap || {};

	// Không tìm thấy sitemap nào (robots không khai báo + /sitemap.xml không có).
	if (!sm.found) {
		return (
			<div className={cn('tab')}>
				<div className={cn('empty')}>
					<FontAwesomeIcon icon="fa-light fa-sitemap" />
					<p>
						Không tìm thấy sitemap. robots.txt {sm.from_robots ? 'có khai báo nhưng không tải được' : 'không khai báo sitemap'} và
						<code> /sitemap.xml</code> cũng không tồn tại. Nên tạo sitemap và khai báo trong robots.txt:
						<code>Sitemap: https://.../sitemap.xml</code>.
					</p>
				</div>
				{Array.isArray(sm.declared) && sm.declared.length > 0 && (
					<>
						<h4 className={cn('sectionTitle')}>Sitemap khai báo trong robots.txt</h4>
						<ul className={cn('sitemapList')}>
							{sm.declared.map((s, i) => <li key={i}><UrlLink url={s} /></li>)}
						</ul>
					</>
				)}
			</div>
		);
	}

	const s = sm.summary || {};

	const cards = [
		{label: 'URL trong sitemap', value: s.sitemap_urls, icon: 'fa-light fa-sitemap', color: '#4f46e5'},
		{label: 'Khớp & hợp lệ', value: s.ok, icon: 'fa-light fa-circle-check', color: '#16A34A'},
		{label: 'Thiếu trong sitemap', value: s.missing, icon: 'fa-light fa-plus', bad: true},
		{label: 'Sitemap nhưng chưa craw', value: s.not_crawled, icon: 'fa-light fa-magnifying-glass', bad: true},
		{label: 'URL lỗi (4xx/5xx)', value: s.error, icon: 'fa-light fa-triangle-exclamation', bad: true},
		{label: 'URL bị chuyển hướng', value: s.redirect, icon: 'fa-light fa-arrows-turn-right', bad: true},
		{label: 'URL noindex', value: s.noindex, icon: 'fa-light fa-ban', bad: true},
	];

	const urlCol = {title: 'URL', dataIndex: 'url', ellipsis: true, render: (u) => <UrlLink url={u} />};
	const statusCol = {title: 'Trạng thái', dataIndex: 'status', width: 120, render: (st) => <StatusTag status={st} />};
	const pager = {pageSize: 15, showSizeChanger: false, hideOnSinglePage: true};

	const section = (title, hint, columns, rows) => (
		<>
			<h4 className={cn('sectionTitle')}>{title} ({fmt(rows.length)})</h4>
			{rows.length === 0 ? (
				<p className={cn('muted')}>{hint.ok}</p>
			) : (
				<>
					<p className={cn('muted')}>{hint.warn}</p>
					<Table size="small" rowKey={(r, i) => i} columns={columns} dataSource={rows} scroll={{x: 'max-content'}} pagination={pager} />
				</>
			)}
		</>
	);

	return (
		<div className={cn('tab')}>
			<div className={cn('statGrid')}>
				{cards.map((c) => {
					const danger = c.bad && Number(c.value) > 0;
					const color = danger ? '#DC2626' : (c.color || '#16A34A');
					return (
						<div className={cn('statCard', {danger})} key={c.label}>
							<div className={cn('statIcon')} style={{color}}><FontAwesomeIcon icon={c.icon} /></div>
							<div>
								<div className={cn('statValue')} style={{color}}>{fmt(c.value)}</div>
								<div className={cn('statLabel')}>{c.label}</div>
							</div>
						</div>
					);
				})}
			</div>

			<h4 className={cn('sectionTitle')}>Nguồn sitemap ({(sm.sources || []).length})</h4>
			<ul className={cn('sitemapList')}>
				{(sm.sources || []).map((src, i) => (
					<li key={i}><UrlLink url={src.url} /> — {fmt(src.count)} URL</li>
				))}
			</ul>
			{sm.capped && <p className={cn('muted')}>Sitemap rất lớn — chỉ đối chiếu {fmt(s.sitemap_urls)} URL đầu tiên.</p>}
			{s.other_host > 0 && <p className={cn('muted')}>Bỏ qua {fmt(s.other_host)} URL khác tên miền trong sitemap.</p>}

			{section(
				'Thiếu trong sitemap',
				{
					ok: 'Mọi trang crawl được (2xx, index) đều đã có trong sitemap.',
					warn: 'Trang crawl được (2xx, cho index) nhưng KHÔNG có trong sitemap — nên bổ sung để Google phát hiện đầy đủ.',
				},
				[urlCol], sm.missing || [],
			)}

			{section(
				'Trong sitemap nhưng chưa craw tới',
				{
					ok: 'Mọi URL sitemap đều được crawler tiếp cận.',
					warn: 'URL có trong sitemap nhưng crawler không tới được (có thể là trang mồ côi/không được liên kết, hoặc vượt giới hạn số trang của lần crawl).',
				},
				[urlCol], sm.not_crawled || [],
			)}

			{section(
				'URL lỗi trong sitemap',
				{
					ok: 'Không có URL lỗi nào trong sitemap.',
					warn: 'URL trả 4xx/5xx — không nên khai báo trong sitemap. Hãy gỡ khỏi sitemap hoặc sửa trang.',
				},
				[urlCol, statusCol], sm.error || [],
			)}

			{section(
				'URL bị chuyển hướng trong sitemap',
				{
					ok: 'Không có URL chuyển hướng nào trong sitemap.',
					warn: 'URL bị redirect — sitemap nên trỏ thẳng tới URL đích cuối cùng (200) thay vì URL trung gian.',
				},
				[urlCol, {title: 'Chuyển tới (đích cuối)', dataIndex: 'to', ellipsis: true, render: (u) => <UrlLink url={u} />}],
				sm.redirect || [],
			)}

			{section(
				'URL noindex trong sitemap',
				{
					ok: 'Không có URL noindex nào trong sitemap.',
					warn: 'URL đặt noindex nhưng vẫn nằm trong sitemap — tín hiệu mâu thuẫn với Google. Gỡ khỏi sitemap hoặc bỏ noindex.',
				},
				[urlCol], sm.noindex || [],
			)}
		</div>
	);
}

export default TechSitemap;
